/*
 * MSH 2026-10-01 — show only the chosen colourway's photos.
 *
 * May: "when I clicked white sole only the first image shows white sole."
 * Then: "can we make it instant change."
 *
 * Every shoe is uploaded with a full set of angles per sole colour, e.g.
 *   ...-Activesneakers-Blk-STR1-NLB.jpg  ... -Blk-STR8-...
 *   ...-Activesneakers-WT-STR1-NLB.jpg   ... -WT-STR8-...
 * Shopify assigns each VARIANT its own featured image, so picking "White Sole"
 * correctly swaps the lead photo — but the gallery still lists all 16 files, so
 * every angle after the first was still the black-soled pair.
 *
 * Nothing is wrong with the product data, so this is fixed in the theme: when a
 * colour is selected, the gallery hides any photo whose colour token does not
 * match. The token is read from the file name (the part right before -STRn), so
 * no colour names are hard-coded and products with a single colourway — every
 * boot, whose files all carry the same token — are left completely alone.
 *
 * TWO paths, on purpose:
 *   1. INSTANT. On pointerdown/click of a colour swatch we already know which
 *      value was picked, so the gallery is filtered in the same frame from a
 *      small {option value -> file token} map built from the product's own JSON
 *      (fetched once per product, on first hover/touch of the picker, and
 *      cached). Waiting for the theme costs ~330 ms — measured on the live site
 *      — because it re-fetches the variant from Shopify before it rewrites
 *      data-product-variant-media.
 *   2. AUTHORITY. The old path still runs whenever that attribute changes, so
 *      anything the instant guess cannot cover (deep links, a size that is only
 *      stocked in one colour, the theme correcting itself) still lands right.
 *
 * MSH 2026-10-19 - BY-NAME FALLBACK. JB: "when I add to cart [Sneakers V2],
 * white doesn't reflect the shoes. I mean white sole." On Sneakers V2 (104) and
 * Classic Shoes (25) the listings came back from YWF with the variant photos
 * assigned by STYLE (Women's -> Blk, Men's -> WT) or all Blk, so the variant
 * data cannot say which photo belongs to which sole. The gallery files still
 * can: every one of those products carries a full -Blk- and -WT- set. So when
 * the variant map is useless, the option VALUE is matched to a file token by
 * letters-in-order ("White Sole" ~ wt, "Black Sole" ~ blk, "Cream White Sole"
 * ~ cw), restricted to the colour option and only when it pairs up one-to-one.
 * For those products the checked colour radio, not data-product-variant-media
 * (which is wrong there), is the authority.
 *
 * Works on the product page and inside the collection-page size chooser, since
 * both render <add-to-cart-component data-product-variant-media="..."> and a
 * <product-form-component data-product-url="...">.
 */
(function () {
  var TOKEN = /-([A-Za-z]{1,6})-STR\d+/i;

  /** {handle: {optionValueLowerCase: token}} — one fetch per product, ever. */
  var maps = {};
  var pending = {};

  function tokenOf(url) {
    if (!url) return null;
    var m = String(url).match(TOKEN);
    return m ? m[1].toLowerCase() : null;
  }

  /** "wt" ~ "white sole", "blk" ~ "black sole", "cw" ~ "cream white sole":
   *  every letter of the token, in order, inside the value. */
  function lettersInOrder(token, value) {
    var i = 0;
    for (var j = 0; j < value.length && i < token.length; j++) {
      if (value[j] === token[i]) i++;
    }
    return i === token.length;
  }

  /**
   * MSH 2026-10-19: build {colour value -> token} from the option values and
   * the gallery file names when the variants cannot be trusted. Returns null
   * unless at least two tokens each pair with exactly one value and vice versa.
   */
  function mapByName(product) {
    var tokens = {};
    // /products/<handle>.js lists images as plain URL strings (products.json
    // gives objects with .src) - take either.
    (product.images || []).forEach(function (img) {
      var t = tokenOf(typeof img === 'string' ? img : img && img.src);
      if (t) tokens[t] = true;
    });
    var tokenList = Object.keys(tokens);
    if (tokenList.length < 2) return null;

    var colour = (product.options || []).filter(function (o) {
      return /colou?r|sole/i.test(o.name || '');
    })[0];
    if (!colour || !colour.values) return null;

    var map = {};
    var used = {};
    for (var v = 0; v < colour.values.length; v++) {
      var value = String(colour.values[v]).toLowerCase();
      var hits = tokenList.filter(function (t) {
        return lettersInOrder(t, value);
      });
      if (hits.length !== 1) return null; // ambiguous or unmatched value
      if (used[hits[0]]) return null; // two values claim one token
      used[hits[0]] = true;
      map[value] = hits[0];
    }
    if (Object.keys(used).length < 2) return null;
    return map;
  }

  /** The colour the shopper has actually ticked, for by-name products. */
  function checkedToken(root, map) {
    var inputs = (root.querySelectorAll ? root : document).querySelectorAll(
      'input[type="radio"]:checked, select option:checked'
    );
    for (var i = 0; i < inputs.length; i++) {
      var t = map[String(inputs[i].value).toLowerCase()];
      if (t) return t;
    }
    return null;
  }

  function rootFor(el) {
    return el.closest('.quick-add-modal') || el.closest('.shopify-section') || document;
  }

  function mediaItemsFor(root) {
    return root.querySelectorAll(
      '.product-media-container, .media-gallery li, .media-gallery__item, slideshow-slide'
    );
  }

  function clear(root) {
    (root.querySelectorAll ? root : document)
      .querySelectorAll('[data-msh-colour-hidden]')
      .forEach(function (item) {
        item.removeAttribute('data-msh-colour-hidden');
      });
  }

  /** How many gallery items the page is actually DRAWING right now. */
  function drawnCount(items) {
    var n = 0;
    items.forEach(function (item) {
      if (!item.hasAttribute('data-msh-colour-hidden') && item.getBoundingClientRect().height > 1) n++;
    });
    return n;
  }

  /**
   * Hide every gallery item whose colour token is not `want`.
   * Bails out unless the gallery actually mixes colourways, so single-colour
   * products (all the boots) keep every angle.
   */
  function filter(root, want) {
    if (!want) return;
    var items = mediaItemsFor(root);
    if (!items.length) return;

    var seen = {};
    var tokens = [];
    items.forEach(function (item) {
      var img = item.querySelector('img');
      var t = img && tokenOf(img.getAttribute('src') || img.currentSrc);
      tokens.push(t);
      if (t) seen[t] = true;
    });
    if (Object.keys(seen).length < 2) return;

    var drawnBefore = drawnCount(items);

    items.forEach(function (item, i) {
      if (!tokens[i]) return; // not one of ours — leave it be
      item.toggleAttribute('data-msh-colour-hidden', tokens[i] !== want);
    });

    /* SAFETY NET, MSH 2026-10-02. May: "product images are not loading for
       Sneakers V2."

       Those listings came back from YWF with EVERY variant pointing at a
       black-sole photo, white-sole variants included, so this script was told
       the shopper wanted "Blk" while the gallery was drawing the WT set - and
       it hid every photo the page was showing. 104 products with an empty
       gallery.

       A colourway filter must never be able to empty a gallery, whatever the
       product data says. So: count what the page is drawing before and after.
       If the page was drawing something and now draws nothing, the match was
       wrong - put everything back and show all the angles, which is exactly
       how this script already treats a single-colour product.

       The before-count also makes this self-calibrating: inside a quick-add
       modal that has not been shown yet nothing is drawn, the count is 0 both
       times, and the check stays out of the way. */
    if (drawnBefore > 0 && drawnCount(items) === 0) {
      items.forEach(function (item) {
        item.removeAttribute('data-msh-colour-hidden');
      });
    }
  }

  function applyFromComponent(component) {
    var root = rootFor(component);

    // MSH 2026-10-02: skip the products whose variants all point at the same
    // colour's photos (see loadMap) - there is nothing to follow there.
    var map = maps[handleOf(root)];
    if (map && !map.$usable) {
      clear(root);
      return;
    }
    if (map && map.$byName) {
      // The variant attribute lies on these products; the ticked swatch does not.
      filter(root, checkedToken(root, map));
      return;
    }

    filter(root, tokenOf(component.dataset.productVariantMedia));
  }

  function applyAll() {
    document
      .querySelectorAll('add-to-cart-component[data-product-variant-media]')
      .forEach(applyFromComponent);
  }

  /* ---- the instant path ------------------------------------------------ */

  function handleOf(root) {
    var form = root.querySelector
      ? root.querySelector('product-form-component[data-product-url]')
      : null;
    return form ? form.dataset.productUrl : null;
  }

  /** Build {option value: token} from the product's variants. Cached. */
  function loadMap(url) {
    if (!url) return Promise.resolve(null);
    if (maps[url]) return Promise.resolve(maps[url]);
    if (pending[url]) return pending[url];

    pending[url] = fetch(url + '.js')
      .then(function (r) {
        return r.json();
      })
      .then(function (product) {
        var map = {};
        (product.variants || []).forEach(function (v) {
          var t = tokenOf(v.featured_image && v.featured_image.src);
          if (!t) return;
          (v.options || []).forEach(function (value) {
            var key = String(value).toLowerCase();
            // Only keep a value that always means the same token, so "US 6"
            // (which exists in both colours) can never mislead us.
            if (map[key] === undefined) map[key] = t;
            else if (map[key] !== t) map[key] = null;
          });
        });
        /* MSH 2026-10-02: can this product's own data tell its colourways
           apart at all? Every Sneakers V2 variant carries a -Blk- featured
           image, so the answer there is no, and filtering by it would be
           guesswork. Needs at least two different tokens across the option
           values that resolve to one. */
        var tokens = {};
        /* MSH 2026-10-19: judge the COLOUR option's values, not every value.
           On Sneakers V2 "Women's" -> Blk and "Men's" -> WT resolve (the
           photos were assigned by style), which used to pass this test while
           "White Sole" itself resolved to nothing. */
        var colourOpt = (product.options || []).filter(function (o) {
          return /colou?r|sole/i.test(o.name || '');
        })[0];
        var judge = colourOpt && colourOpt.values ? colourOpt.values.map(function (v) {
          return String(v).toLowerCase();
        }) : Object.keys(map);
        judge.forEach(function (key) {
          if (map[key]) tokens[map[key]] = true;
        });
        map.$usable = Object.keys(tokens).length > 1;

        /* MSH 2026-10-19: variants useless (Sneakers V2, Classic Shoes) - let
           the option values and the file names pair themselves up instead. */
        if (!map.$usable) {
          var byName = mapByName(product);
          if (byName) {
            map = byName;
            map.$usable = true;
            map.$byName = true;
          }
        }

        maps[url] = map;
        if (!map.$usable) clear(document); // undo anything applied meanwhile
        else applyAll(); // re-run with the map now in hand
        return map;
      })
      .catch(function () {
        maps[url] = { $usable: false };
        return maps[url];
      });

    return pending[url];
  }

  /**
   * Warm the map as soon as the shopper goes anywhere near a choice: hovering a
   * product card on the collection page, or the picker itself. By the time the
   * quick-add chooser has opened the map is already in hand, so her first click
   * on a sole colour is as instant as the second.
   */
  function warm(event) {
    var el = event.target;
    if (!el || !el.closest) return;

    if (el.closest('variant-picker, .variant-option')) {
      loadMap(handleOf(rootFor(el)));
      return;
    }
    var card = el.closest('.product-card, .card-gallery, quick-add-component');
    var link = card && card.querySelector('a[href*="/products/"]');
    if (link) loadMap(link.getAttribute('href').split(/[?#]/)[0]);
  }

  /**
   * The swatch she actually clicks is the LABEL, not the radio inside it, and
   * the theme does not always let the radio's own click through — so resolve
   * the label to its control rather than waiting for an event on the input.
   */
  function inputFrom(el) {
    if (!el || !el.closest) return null;
    var direct = el.closest('input[type="radio"][value]');
    if (direct) return direct;

    var label = el.closest('label');
    if (!label) return null;
    if (label.control && label.control.type === 'radio') return label.control;
    if (label.htmlFor) {
      var byId = document.getElementById(label.htmlFor);
      if (byId && byId.type === 'radio') return byId;
    }
    return label.querySelector('input[type="radio"][value]');
  }

  function pick(event) {
    var input = inputFrom(event.target);
    if (!input || !input.value) return;

    var root = rootFor(input);
    var url = handleOf(root);
    var value = String(input.value).toLowerCase();
    var map = maps[url];
    if (map) {
      if (map.$usable && map[value]) filter(root, map[value]);
      return;
    }
    // First click on a product we have not fetched yet: apply as soon as the
    // map lands, which still beats the theme's own variant round-trip.
    loadMap(url).then(function (m) {
      if (m && m.$usable && m[value]) filter(root, m[value]);
    });
  }

  /* ---- the authority path ---------------------------------------------- */

  var observer = new MutationObserver(function (records) {
    var touched = false;
    records.forEach(function (r) {
      if (r.type === 'attributes') {
        applyFromComponent(r.target);
        touched = true;
      } else if (r.addedNodes.length) {
        touched = true;
      }
    });
    if (touched) applyAll();
  });

  function start() {
    applyAll();

    // Capture phase so the gallery is filtered before the theme starts its
    // variant fetch, not after it comes back.
    document.addEventListener('pointerdown', pick, true);
    document.addEventListener('click', pick, true);
    document.addEventListener('change', pick, true);
    document.addEventListener('pointerover', warm, true);
    document.addEventListener('touchstart', warm, { capture: true, passive: true });

    observer.observe(document.body, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['data-product-variant-media'],
    });

    // Have the map ready before the first hover on a product page, where the
    // picker is the whole point of the page.
    var idle = window.requestIdleCallback || function (fn) { setTimeout(fn, 400); };
    idle(function () {
      document.querySelectorAll('product-form-component[data-product-url]').forEach(function (f) {
        if (f.closest('.quick-add-modal')) return; // the chooser warms on hover
        loadMap(f.dataset.productUrl);
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
